"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { gsap, ScrollTrigger } from "@/lib/gsap";
import { throttleRAF } from "@/lib/helper";

interface TabItem {
  title: string;
  content: string;
  image: string;
  alt: string;
}

interface ContentCreationData {
  tagline: string;
  title: string;
  description: string;
  tabs: TabItem[];
}

export default function ContentCreation({
  data,
}: {
  data: ContentCreationData;
}) {
  const { tabs, tagline, title, description } = data;
  const [activeTab, setActiveTab] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleTabChange = (index: number) => {
    if (activeTab !== index && !isTransitioning) {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveTab(index);
        setTimeout(() => setIsTransitioning(false), 200);
      }, 250);
    }
  };
  const sectionRef = useRef<HTMLElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (!sectionRef.current) return;
    const ctx = gsap.context(() => {
      const section = sectionRef.current;
      const label = labelRef.current;
      const heading = headingRef.current;

      if (!section || !label || !heading) return;

      gsap.set([label, heading], {
        opacity: 0,
        y: 40,
        filter: "blur(8px)",
      });

      const tabButtons = section.querySelectorAll(
        '[data-animate="tab-button"]'
      );
      const contentElements = section.querySelectorAll(
        '[data-animate="content"]'
      );

      gsap.set(tabButtons, {
        opacity: 0,
        x: -10,
        filter: "blur(8px)",
      });

      gsap.set(contentElements, {
        opacity: 0,
        y: 40,
        filter: "blur(8px)",
      });

      gsap.to([label, heading], {
        opacity: 1,
        y: 0,
        filter: "blur(0px)",
        duration: 0.7,
        ease: "power2.out",
        stagger: 0.1,
        scrollTrigger: {
          trigger: section,
          start: "top 80%",
          end: "top 50%",
          toggleActions: "play none none reverse",
          markers: false,
        },
      });
      const isMobile = window.innerWidth <= 768;
      gsap.to(tabButtons, {
        opacity: 1,
        x: 0,
        duration: 0.6,
        filter: "blur(0px)",
        stagger: 0.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: isMobile ? tabButtons : section,
          start: isMobile ? "top 90%" : "top 70%",
          end: "top 40%",
          toggleActions: "play none none reverse",
          markers: false,
        },
      });

      gsap.to(contentElements, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        filter: "blur(0px)",
        ease: "power2.out",
        scrollTrigger: {
          trigger: isMobile ? contentElements : section,
          start: isMobile ? "top 80%" : "top 70%",
          end: "top 20%",
          toggleActions: "play none none reverse",
          markers: false,
        },
      });
    }, sectionRef);
    const handleResize = throttleRAF(() => {
      requestAnimationFrame(() => {
        ScrollTrigger.refresh();
      });
    }, 250);

    window.addEventListener("resize", handleResize);

    return () => {
      ctx.revert();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="content-creation-section"
      className="py-[70px] bg-theme-secondary text-white"
    >
      <div className="container">
        <div className="mb-12">
          <div className="">
            <div
              ref={labelRef}
              data-animate="label"
              className="flex items-center mb-2"
            >
              <div className="w-5 h-0.5 bg-theme-primary mr-3"></div>
              <p className="text-sm font-semibold text-theme-primary tracking-wider uppercase">
                {tagline}
              </p>
            </div>

            <h2
              ref={headingRef}
              data-animate="heading"
              className="text-4xl lg:text-5xl font-semibold text-white capitalize mb-4"
            >
              {title}
            </h2>

            {description && (
              <p className="mb-6 text-sm md:text-base text-gray-300">
                {description}
              </p>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div
              data-animate="tab-button"
              className="flex flex-col border-t border-b border-white/20"
            >
              {tabs.map((tab, index) => (
                <button
                  data-animate="tab-button"
                  key={index}
                  onClick={() => handleTabChange(index)}
                  className={cn(
                    "text-left py-6 text-2xl font-semibold transition-all duration-500 px-4 -mx-4 border-b border-white/20 last:border-b-0 relative group cursor-pointer",
                    "hover:bg-white/5 hover:text-white hover:shadow-lg hover:shadow-white/10 hover:scale-[1.02]",
                    "hover:after:absolute hover:after:bottom-0 hover:after:left-0 hover:after:w-full hover:after:h-0.5 hover:after:bg-gradient-to-r hover:after:from-transparent hover:after:via-white/50 hover:after:to-transparent",
                    activeTab === index
                      ? "text-white bg-gradient-to-r from-white/10 to-white/5 border-l-4 border-l-white shadow-lg shadow-white/10 scale-[1.02] after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-gradient-to-r after:from-blue-400/50 after:via-white/50 after:to-purple-400/50"
                      : "text-gray-400"
                  )}
                  disabled={isTransitioning}
                >
                  {tab.title}
                </button>
              ))}
            </div>

            <div className=" mt-8 overflow-hidden">
              <div
                data-animate="content"
                className={cn(
                  "transition-all duration-500 ease-out",
                  isTransitioning
                    ? "opacity-0 translate-x-4 scale-98"
                    : "opacity-100 translate-x-0 scale-100"
                )}
              >
                <p className="text-lg text-transparent bg-clip-text bg-gradient-to-r from-gray-300 via-white to-gray-300 leading-relaxed">
                  {tabs[activeTab].content}
                </p>
              </div>
            </div>
          </div>

          <div data-animate="content">
            <div className="flex items-center justify-center">
              <div className="relative group">
                <div className="relative rounded-2xl overflow-hidden max-w-md w-full p-1 bg-gradient-to-r from-blue-400/20 via-purple-400/20 to-blue-400/20">
                  <div
                    className={cn(
                      "transition-all duration-700 ease-in-out rounded-2xl overflow-hidden bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm",
                      isTransitioning
                        ? "opacity-0 scale-90 rotate-2 blur-sm translate-y-4"
                        : "opacity-100 scale-100 rotate-0 blur-0 translate-y-0"
                    )}
                  >
                    {tabs[activeTab].image && (
                      <Image
                        src={tabs[activeTab].image}
                        alt={tabs[activeTab].alt}
                        quality={85}
                        sizes="(max-width: 768px) 300px, (max-width: 1024px) 340px, 448px"
                        className="object-cover w-[300px] h-[330px] sm:w-[340px] sm:h-[380px] lg:w-[448px] lg:h-[492px] max-w-[300px] max-h-[330px] sm:max-w-[340px] sm:max-h-[380px] lg:max-w-[448px] lg:max-h-[492px] object-center transition-all duration-500 ease-in-out group-hover:scale-105 group-hover:brightness-110"
                        width={448}
                        height={492}
                      />
                    )}
                  </div>
                  <div
                    className={cn(
                      "absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent rounded-2xl transition-all duration-700 transform",
                      isTransitioning
                        ? "translate-x-full opacity-100"
                        : "translate-x-[-100%] opacity-0"
                    )}
                  />
                  <div
                    className={cn(
                      "absolute inset-0 rounded-2xl transition-all duration-500",
                      isTransitioning
                        ? "shadow-lg shadow-blue-400/20"
                        : "shadow-xl shadow-blue-400/30"
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
